import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { Check, Camera, Gift, Star, ArrowRight, Users, Clock, Video, X } from "lucide-react";
import { getCreditsBalance } from "../../services/api";
import * as Sentry from "@sentry/react";
import { PaymentError } from "../../errors/sentryErrors";
import {
  copyReviewText,
  openGoogleReviewHandoffWindow,
  completeGoogleReviewHandoff,
} from "../../utils/googleReview";

// What a submitted review lands on — both straight after submitting and whenever
// the traveller opens the URL again later. It replaces the locked, greyed-out form
// that used to sit here, which showed a page full of inputs nobody could edit.
//
// Everything on this screen is real: counts come from the trip's own media, credits
// from the rewards preview and the credits ledger. There are deliberately no
// invented "levels" or "points to next tier" — nothing in the API backs those.
const REJECTION_REASON_LABELS = {
  spam: "Identified as spam",
  low_quality: "Low quality content",
  offensive: "Offensive content",
  misleading: "Misleading information",
  personal_info: "Contains personal information",
  copyright: "Copyright concern",
  duplicate: "Duplicate submission",
  other: "Other",
};

export default function ReviewThankYou({ trip, review, post, mediaList = [], rewards }) {
  const navigate = useNavigate();
  const [balance, setBalance] = useState(null);

  useEffect(() => {
    let cancelled = false;
    getCreditsBalance()
      .then((data) => { if (!cancelled) setBalance(data); })
      .catch((err) => {
        Sentry.withScope((scope) => {
          scope.setTag("feature", "payment");
          scope.setTag("app", "web");
          scope.setTag("error_source", err instanceof TypeError ? "network" : "server");
          Sentry.captureException(new PaymentError(err instanceof Error ? err.message : String(err)));
        });
      });
    return () => { cancelled = true; };
  }, []);

  const photos = mediaList.filter((m) => m.kind === "photo");
  const videos = mediaList.filter((m) => m.kind === "video");

  const nights = (() => {
    if (!trip?.startDate || !trip?.endDate) return null;
    const from = new Date(trip.startDate);
    const to = new Date(trip.endDate);
    if (Number.isNaN(from.getTime()) || Number.isNaN(to.getTime())) return null;
    return Math.max(0, Math.round((to - from) / 86400000));
  })();

  const coverPhoto =
    photos.find((m) => m.category === "cover") || photos[0] || null;
  const coverUrl = coverPhoto?.publicUrl || coverPhoto?.url || coverPhoto?.thumbUrl || null;

  const postStatus = post?.status || "pending";
  const isApproved = postStatus === "approved";
  const isRejected = postStatus === "rejected";
  const rejectionLabel = REJECTION_REASON_LABELS[post?.rejectionReason] || post?.rejectionReason;

  const earned = rewards?.earned || 0;
  const currency = rewards?.currency || "RoamPoints";
  const pending = Math.max(0, (rewards?.potential || 0) - earned);

  // Nothing is credited until a moderator approves the post, so straight after
  // submitting there is genuinely nothing earned yet. Leading with a bare "+0"
  // reads as a bug and as though the review was worth nothing — so when the
  // whole amount is still to come, the headline shows what approval will pay
  // instead, and says plainly that it lands on approval. A rejected post never
  // pays out, so that case gets its own headline rather than either of these.
  const awaitingApproval = !isRejected && earned === 0 && pending > 0;
  const headlineAmount = awaitingApproval ? pending : earned;
  const headlineLabel = awaitingApproval ? "RoamPoints on approval" : "RoamPoints earned";

  const firstName = (review?.userName || "").trim().split(" ")[0];
  const reviewText = (review?.description || "").trim();

  const finalStepLabel = isApproved ? "Approved" : isRejected ? "Rejected" : "Awaiting approval";
  const finalStepBadge = isApproved ? "Live" : isRejected ? "Rejected" : "In review";

  const steps = [
    { label: "Review submitted", done: true },
    {
      label: photos.length > 0 || videos.length > 0 ? "Memories uploaded" : "No photos added",
      done: photos.length > 0 || videos.length > 0,
    },
    {
      label: finalStepLabel,
      done: isApproved,
      failed: isRejected,
      pendingLabel: isApproved ? null : finalStepBadge,
    },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-teal-50/60 via-white to-white pt-24 pb-16">
      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
          {/* LEFT — the thank you and what happens next */}
          <div className="lg:col-span-7">
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-teal-600 text-white text-[11px] font-bold uppercase tracking-wider">
              <Check className="w-3 h-3" strokeWidth={3} /> Review submitted
            </span>

            <h1 className="mt-4 text-3xl sm:text-4xl font-bold text-teal-900 leading-tight">
              Thank you{firstName ? `, ${firstName}` : ""}!
            </h1>
            <p className="mt-2 text-gray-600 leading-relaxed max-w-lg">
              {isApproved
                ? "Your travel story is live on the community page, where other roamers can plan their next trip from it."
                : isRejected
                ? "Our team reviewed your post and it wasn't approved this time."
                : "You've shared your travel story. Our team is reviewing it now — once it's approved it goes live on the community page, where other roamers can plan their next trip from it."}
            </p>

            {isRejected && (
              <div className="mt-4 rounded-2xl border border-red-100 bg-red-50 p-4">
                <p className="text-sm font-semibold text-red-800">
                  Reason: {rejectionLabel || "Not specified"}
                </p>
                {post?.rejectionNote && (
                  <p className="mt-1 text-sm text-red-700">{post.rejectionNote}</p>
                )}
              </div>
            )}

            {/* Progress */}
            <div className="mt-7 bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
              <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-4">
                Progress
              </p>
              <ul className="space-y-3">
                {steps.map((step) => (
                  <li key={step.label} className="flex items-center gap-3">
                    <span
                      className={`w-5 h-5 rounded-full flex items-center justify-center shrink-0 ${
                        step.done
                          ? "bg-teal-600 text-white"
                          : step.failed
                          ? "bg-red-100 text-red-700"
                          : "bg-amber-100 text-amber-700"
                      }`}
                    >
                      {step.done ? (
                        <Check className="w-3 h-3" strokeWidth={3} />
                      ) : step.failed ? (
                        <X className="w-3 h-3" strokeWidth={3} />
                      ) : (
                        <Clock className="w-3 h-3" />
                      )}
                    </span>
                    <span
                      className={`text-sm ${
                        step.done ? "text-gray-700" : step.failed ? "text-red-700" : "text-gray-500"
                      }`}
                    >
                      {step.label}
                    </span>
                    {step.pendingLabel && (
                      <span
                        className={`ml-auto text-[10px] font-bold uppercase tracking-wider ${
                          step.failed ? "text-red-600" : "text-amber-600"
                        }`}
                      >
                        {step.pendingLabel}
                      </span>
                    )}
                  </li>
                ))}
              </ul>
            </div>

            {/* Credits — real ledger figures, no invented tiers. A rejected post
                never pays out, so it gets a plain notice instead of a "+0" inside
                the same gift-styled panel used for a genuine reward. */}
            {isRejected ? (
              <div className="mt-5 rounded-2xl border border-gray-200 bg-gray-50 p-6">
                <p className="text-sm font-semibold text-gray-700">
                  No RoamPoints were awarded for this submission.
                </p>
                {balance && (
                  <p className="mt-3 pt-3 border-t border-gray-200 text-xs text-gray-500">
                    Wallet balance:{" "}
                    <span className="font-bold text-gray-700">{balance.balance} {currency}</span>
                  </p>
                )}
              </div>
            ) : (
              rewards && (
                <div className="mt-5 rounded-2xl bg-gradient-to-br from-teal-700 to-teal-800 text-white p-6">
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <p className="text-[10px] font-bold text-teal-100 uppercase tracking-wider">
                        {headlineLabel}
                      </p>
                      <p className="text-4xl font-bold leading-tight">
                        +{headlineAmount}
                        <span className="text-base font-semibold text-teal-200"> {currency}</span>
                      </p>
                      {awaitingApproval ? (
                        <p className="text-xs text-teal-100 mt-1.5">
                          Added to your wallet once a moderator approves your post.
                        </p>
                      ) : (
                        pending > 0 && (
                          <p className="text-xs text-teal-100 mt-1.5">
                            <span className="font-bold text-amber-300">+{pending}</span> more once
                            your post and photos are approved
                          </p>
                        )
                      )}
                    </div>
                    <span className="w-11 h-11 rounded-full bg-white/15 flex items-center justify-center shrink-0">
                      <Gift className="w-5 h-5" />
                    </span>
                  </div>

                  {balance && (
                    <p className="mt-4 pt-4 border-t border-white/15 text-xs text-teal-100">
                      Wallet balance:{" "}
                      <span className="font-bold text-white">{balance.balance} {currency}</span>
                    </p>
                  )}
                </div>
              )
            )}

            {/* Google hand-off. Google's composer cannot be pre-filled from a URL,
                so the review text goes to the clipboard and we say so. */}
            <div className="mt-5 bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
              <p className="text-sm font-semibold text-gray-900">Share it on Google too</p>
              <p className="text-xs text-gray-500 mt-1 leading-relaxed">
                It takes one paste and helps other travellers find us.
              </p>
              <div className="mt-4 flex">
                <button
                  type="button"
                  onClick={() => {
                    // Opened synchronously, inside the click gesture, so the
                    // window.open() isn't treated as a blocked popup.
                    const handoffWindow = openGoogleReviewHandoffWindow();
                    copyReviewText(reviewText || "").then((ok) => {
                      toast[ok ? "success" : "error"](
                        ok
                          ? "Review copied — paste it into the Google tab that just opened."
                          : "Couldn't copy your review. Select and copy it manually before pasting into Google.",
                        { toastId: "copy-review" },
                      );
                    });
                    completeGoogleReviewHandoff(handoffWindow, reviewText || "");
                  }}
                  className="flex-1 px-4 py-2.5 bg-teal-700 text-white text-sm font-bold rounded-xl hover:bg-teal-800 transition-colors text-center cursor-pointer"
                >
                  Write a Google review
                </button>
              </div>
            </div>

            <div className="mt-6 flex flex-wrap gap-3">
              <button
                type="button"
                onClick={() => navigate("/dashboard")}
                className="px-6 py-3 bg-teal-700 text-white font-bold rounded-xl shadow-sm hover:bg-teal-800 transition-colors flex items-center gap-2 cursor-pointer"
              >
                View my trips <ArrowRight className="w-4 h-4" />
              </button>
              <button
                type="button"
                onClick={() => navigate("/community")}
                className="px-6 py-3 bg-white border border-gray-200 text-gray-700 font-semibold rounded-xl hover:bg-gray-50 transition-colors cursor-pointer"
              >
                Explore community
              </button>
            </div>
          </div>

          {/* RIGHT — the trip as it will appear */}
          <div className="lg:col-span-5">
            <div className="bg-white rounded-3xl border border-gray-100 shadow-sm overflow-hidden">
              <div className="relative h-52 bg-gray-100">
                {coverUrl ? (
                  <img src={coverUrl} alt="" className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex flex-col items-center justify-center gap-2 text-gray-300 bg-gradient-to-br from-gray-50 to-gray-100">
                    <Camera className="w-8 h-8" />
                    <span className="text-xs font-medium text-gray-400">No photos added</span>
                  </div>
                )}
                {review?.overallRating > 0 && (
                  <span className="absolute top-3 right-3 px-2.5 py-1 rounded-full bg-white/95 text-xs font-bold text-gray-900 flex items-center gap-1 shadow-sm">
                    <Star className="w-3 h-3 text-amber-400 fill-amber-400" />
                    {review.overallRating}.0
                  </span>
                )}
              </div>

              <div className="p-6">
                <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">
                  Destination
                </p>
                <h2 className="text-xl font-bold text-gray-900 leading-snug mt-0.5">
                  {trip?.destination || "Your trip"}
                </h2>

                <dl className="mt-5 space-y-3">
                  {nights !== null && (
                    <div className="flex items-center justify-between text-sm">
                      <dt className="text-gray-500">Duration</dt>
                      <dd className="font-semibold text-gray-900">
                        {nights} {nights === 1 ? "night" : "nights"}
                      </dd>
                    </div>
                  )}
                  <div className="flex items-center justify-between text-sm">
                    <dt className="text-gray-500">Memories</dt>
                    <dd className="font-semibold text-gray-900 flex items-center gap-2">
                      <span className="flex items-center gap-1">
                        <Camera className="w-3.5 h-3.5 text-gray-400" />
                        {photos.length}
                      </span>
                      {videos.length > 0 && (
                        <span className="flex items-center gap-1">
                          <Video className="w-3.5 h-3.5 text-gray-400" />
                          {videos.length}
                        </span>
                      )}
                    </dd>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <dt className="text-gray-500">Status</dt>
                    <dd>
                      <span
                        className={`px-2 py-0.5 rounded-full text-[11px] font-bold ${
                          isApproved
                            ? "bg-teal-50 text-teal-700"
                            : isRejected
                            ? "bg-red-50 text-red-700"
                            : "bg-amber-50 text-amber-700"
                        }`}
                      >
                        {isApproved ? "Live" : isRejected ? "Rejected" : "In review"}
                      </span>
                    </dd>
                  </div>
                </dl>

                {reviewText && (
                  <p className="mt-5 pt-5 border-t border-gray-100 text-sm text-gray-600 leading-relaxed line-clamp-4">
                    {reviewText}
                  </p>
                )}
              </div>
            </div>

            {!isRejected && (
              <p className="mt-4 text-xs text-gray-400 flex items-start gap-2 px-1">
                <Users className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                {isApproved
                  ? "Other travellers can now discover your trip on the community page."
                  : "You'll get a notification the moment your post goes live."}
              </p>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
